import { pickT } from "@repo/shared";
import {
  ASSET_CLASS_FORECAST_HORIZONS,
  ASSET_CLASS_FORECAST_UNKNOWN_ID,
  type AssetClassForecastHorizon,
  type AssetClassForecastSnapshot,
  selectAssetClassForecastPoints,
} from "@repo/shared/types";
import { useMemo, useState } from "react";
import { NormalizedStackedBarChart } from "@/components/charts/normalized-stacked-bar-chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { type StackedBarDatum, TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { T } from "./asset-class-forecast-chart.i18n";

/** Unknown is always muted gray, never a palette colour — same rule as the pies. */
const UNKNOWN_COLOR = "#9ca3af";

type AssetClassForecastChartProps = {
  snapshot: AssetClassForecastSnapshot;
  showChart?: boolean;
  showTable?: boolean;
  /** QL-313 — which horizons the toggle offers. Absent / empty = all three. */
  enabledHorizons?: AssetClassForecastHorizon[];
  defaultHorizon?: AssetClassForecastHorizon;
  chartColors?: string[] | null;
  language: string;
  height?: number;
};

/**
 * QL-315 — the asset-class mix over time as a 100%-normalised stacked bar,
 * plus the legend table of the selected horizon's closing mix. Only one of the
 * two names the bands: the table wins when it is shown, because it carries the
 * percentages as well as the swatches.
 *
 * The 1Y/5Y/10Y toggle is a window over the stored points, never a re-fetch:
 * all 15 grid points live on one snapshot, so switching horizon is free and
 * cannot show a different merge from the one the advisor just looked at.
 */
export function AssetClassForecastChart({
  snapshot,
  showChart = true,
  showTable = true,
  enabledHorizons,
  defaultHorizon = "10Y",
  chartColors,
  language,
  height = 320,
}: AssetClassForecastChartProps) {
  const t = pickT(T, language);
  // QL-313 — the designer's Time Ranges selection, in canonical order; a
  // default outside it opens on the first enabled range instead.
  const horizons = ASSET_CLASS_FORECAST_HORIZONS.filter(
    (h) => !enabledHorizons?.length || enabledHorizons.includes(h),
  );
  const [activeHorizon, setHorizon] = useState<AssetClassForecastHorizon>(
    horizons.includes(defaultHorizon) ? defaultHorizon : (horizons[0] ?? defaultHorizon),
  );

  const points = useMemo(
    () => selectAssetClassForecastPoints(snapshot.points, activeHorizon),
    [snapshot.points, activeHorizon],
  );

  // Which bands actually carry weight somewhere in the stored points. A colour
  // scale keyed on the whole legend puts a swatch in the chart legend for every
  // band the snapshot knows about, so a collection where nothing is unclassified
  // still advertises "Unknown" under a chart that has none of it.
  const drawnBandIds = useMemo(() => {
    const ids = new Set<string>();
    for (const point of snapshot.points) {
      for (const share of point.shares) {
        if (share.percentage > 0) ids.add(share.assetClassId);
      }
      if (point.unclassifiedPct > 0) ids.add(ASSET_CLASS_FORECAST_UNKNOWN_ID);
    }
    return ids;
  }, [snapshot.points]);

  // One colour per band for the life of the component: assigned from the
  // snapshot's legend (asset_class.order) rather than from the visible points,
  // so a band keeps its colour when the horizon changes and a class that only
  // appears late is not handed a colour already in use.
  const { seriesOrder, seriesColors, colorById } = useMemo(() => {
    const palette = chartColors?.length ? chartColors : TABLEAU20_COLORS;
    const order: string[] = [];
    const colors: string[] = [];
    const byId = new Map<string, string>();
    let paletteIndex = 0;
    for (const entry of snapshot.legend) {
      const color =
        entry.assetClassId === ASSET_CLASS_FORECAST_UNKNOWN_ID
          ? UNKNOWN_COLOR
          : palette[paletteIndex++ % palette.length]!;
      // Colour assignment walks the full legend so a band's colour does not
      // depend on what happens to be drawn, but only drawn bands enter the
      // scale domain.
      byId.set(entry.assetClassId, color);
      if (!drawnBandIds.has(entry.assetClassId)) continue;
      order.push(entry.assetClassName);
      colors.push(color);
    }
    return { seriesOrder: order, seriesColors: colors, colorById: byId };
  }, [snapshot.legend, chartColors, drawnBandIds]);

  const nameById = useMemo(
    () => new Map(snapshot.legend.map((entry) => [entry.assetClassId, entry.assetClassName])),
    [snapshot.legend],
  );

  const data = useMemo<StackedBarDatum[]>(() => {
    const rows: StackedBarDatum[] = [];
    for (const point of points) {
      for (const share of point.shares) {
        if (share.percentage <= 0) continue;
        rows.push({ x: point.label, series: share.assetClassName, value: share.percentage });
      }
      // This chart's Unknown band is the unclassified share only: a named asset
      // class stays its own band even when its equitySharePct is blank, because
      // hiding a configured class is the one thing an asset-class chart must
      // not do. The wider `unknownPct` (which also counts unmeasured classes) is
      // on the snapshot for QL-313's equity chart.
      if (point.unclassifiedPct > 0) {
        rows.push({
          x: point.label,
          series: nameById.get(ASSET_CLASS_FORECAST_UNKNOWN_ID) ?? "Unknown",
          value: point.unclassifiedPct,
        });
      }
    }
    return rows;
  }, [points, nameById]);

  // The table shows the closing mix of the selected horizon — the last bar,
  // which is what the horizon label names.
  const tableRows = useMemo(() => {
    const last = points.at(-1);
    if (!last) return [];
    const rows = last.shares
      .filter((share) => share.percentage > 0)
      .map((share) => ({
        id: share.assetClassId,
        label: share.assetClassName,
        percentage: share.percentage,
      }));
    if (last.unclassifiedPct > 0) {
      rows.push({
        id: ASSET_CLASS_FORECAST_UNKNOWN_ID,
        label: nameById.get(ASSET_CLASS_FORECAST_UNKNOWN_ID) ?? "Unknown",
        percentage: last.unclassifiedPct,
      });
    }
    return rows.sort((a, b) => b.percentage - a.percentage);
  }, [points, nameById]);

  const hasTable = showTable && tableRows.length > 0;

  if (data.length === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noData}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* QL-313 — one enabled range needs no switch. */}
      {horizons.length > 1 && (
        <div className="flex justify-end">
          {/* A view switch over points the snapshot already holds, never a
              persisted value, which is what ToggleGroup is for (DESIGN 14j). */}
          <ToggleGroup
            type="single"
            value={activeHorizon}
            onValueChange={(next) => {
              if (next) setHorizon(next as AssetClassForecastHorizon);
            }}
            className="h-9"
          >
            {horizons.map((option) => (
              <ToggleGroupItem key={option} value={option} className="h-7 px-3 text-xs">
                {option}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {showChart && (
        <NormalizedStackedBarChart
          data={data}
          seriesOrder={seriesOrder}
          seriesColors={seriesColors}
          height={height}
          lang={language}
          // The table below is a legend with numbers on it, so the chart's own
          // legend would name every band a second time. Without the table it
          // is the only thing naming the bands, so it stays.
          showLegend={!hasTable}
        />
      )}

      {hasTable && (
        // The shared `data` tier, like every other chart-plus-shares widget on
        // this page (asset-allocation-chart, xray-pie-chart). A hand-rolled
        // table here read as a different table sitting next to theirs.
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>{t.assetClass}</TableHead>
              <TableHead className="text-right">{activeHorizon}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: colorById.get(row.id) ?? UNKNOWN_COLOR }}
                  />
                </TableCell>
                <TableCell>{row.label}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.percentage.toFixed(1)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
